import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AnswerWrongReason } from '../answer-wrong-feedback.model';
import { answerWrongReasonContent } from '../feedback-popup.content';
import { Commands, Resources, Search } from '@local/client-contracts';
import { ResourcesService } from '@shared/services/resources.service';
import { CheckedResource, FeedbackReasonOptionsComponent } from '../feedback-reason-options/feedback-reason-options.component';
import { WikiCardsService } from 'src/app/bar/services/wikis/wiki-cards.service';
import { isWikiCard } from '../../../utils/results.util';
import { AnswerFeedbackSearchItem } from '../../../models/results-types';
import { SelectItem } from 'primeng/api';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { getModifiers, isEnterKey, isSemicolonKey, KeyName } from '@local/ts-infra';
import { isEqual, upperFirst } from 'lodash';
import { windowSizeObserver } from '@shared/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppService } from '@shared/services/app.service';

@UntilDestroy()
@Component({
  selector: 'answer-feedback',
  templateUrl: './answer-feedback.component.html',
  styleUrls: ['./answer-feedback.component.scss'],
})
export class AnswerFeedbackComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly FREE_TEXT_MIN_LENGTH = 5;
  readonly FREE_TEXT_MAX_LENGTH = 399;
  private checkedResources: Search.ResultResourceItem[];
  private keyHandlerId: string;
  private textAreaFocused: boolean;
  private windowSize$ = windowSizeObserver();
  answerWrongReasonContent: AnswerWrongReason = answerWrongReasonContent;
  answerWrongReasonOptions: SelectItem[];
  selectedAnswerOption: string;
  handleSelectedOption: { title: string; subtitle?: string };
  answerInput: string;
  disableSendButton: boolean = true;

  private _model: AnswerFeedbackSearchItem;
  get model(): AnswerFeedbackSearchItem {
    return this._model;
  }

  @Input()
  set model(value: AnswerFeedbackSearchItem) {
    if (isEqual(value, this.model)) {
      return;
    }
    this._model = value;
    this.resetData();
  }

  @Input() query: string;
  @Input() source: string;

  @Output() closeFeedback = new EventEmitter<string>();
  @Output() focusTextarea = new EventEmitter<boolean>();

  @ViewChild('reasonOptionsList') handleReasonOptionsComponent: FeedbackReasonOptionsComponent;

  constructor(
    private resourcesService: ResourcesService,
    private wikiCardsService: WikiCardsService,
    private keyboardService: KeyboardService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.answerWrongReasonOptions = this.answerWrongReasonContent.options.map((a, index) => ({ value: index, label: a.title }));
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 9);
    this.windowSize$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.checkButtonsWidth();
    });
  }

  ngAfterViewInit(): void {
    this.checkButtonsWidth();
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent) {
    const key = keys[0];
    const modifiers = getModifiers(keys);

    if (isEnterKey(key) && modifiers[0] !== 'shift' && !this.disableSendButton) {
      this.onButtonClick('send');
    }
    if (!this.textAreaFocused) {
      return;
    }
    if (isSemicolonKey(event) || key === 'slash') {
      event.stopPropagation();
    }
  }

  private resetData() {
    this.disableSendButton = true;
    this.selectedAnswerOption = null;
    this.answerInput = null;
  }

  private checkButtonsWidth() {
    const container = document.querySelector('.buttons-container') as HTMLElement;
    const breakpoint = 240;
    if (container.offsetWidth <= breakpoint) {
      container.classList.add('single-column');
    } else {
      container.classList.remove('single-column');
    }
  }

  onButtonClick(type: 'send' | 'cancel') {
    if (this.model?.feedbackType === 'Negative') {
      this.handleReasonOptionsComponent?.sendCheckedResources();
    }
    this.sendFeedback(type);
    this.closeFeedback.emit(type);
  }

  onRadioButtonClick($event) {
    this.selectedAnswerOption = $event.value;
    this.handleSelectedOption = this.answerWrongReasonContent.options[$event.value];
    this.disableSendButton = false;
  }

  handleResourcesChecked(resources: CheckedResource[]): void {
    this.checkedResources = resources;
  }

  onFocusTextarea() {
    this.textAreaFocused = true;
    this.focusTextarea.emit(true);
  }

  onBlurTextarea() {
    this.textAreaFocused = false;
    this.focusTextarea.emit(false);
  }

  onChangeTextarea($event) {
    this.answerInput = $event;
    if (this.answerInput.length > this.FREE_TEXT_MIN_LENGTH) {
      this.disableSendButton = false;
    } else if (!this.selectedAnswerOption) {
      this.disableSendButton = true;
    }
  }

  sendFeedback(type) {
    if (!type || type === 'cancel') {
      this.feedback();
    } else {
      this.feedback(this.selectedAnswerOption, this.answerInput);
    }
  }

  feedback(reason?: string, alternativeResourceUrl?: string) {
    const selectedReason = this.answerWrongReasonContent.options[reason];
    const request: Resources.FeedbackRequest = {
      timestamp: Date.now(),
      searchId: this.model.searchId,
      query: this.query,
      source: 'Web',
      platform: this.source === 'chat' ? 'Chat' : upperFirst(this.appService.platform),
      type: this.model.feedbackType,
      level: 'Response',
      linkId: this.model.linkIds,
      resourceId: this.model.resourceIds,
    };
    if (selectedReason) {
      request.reason = selectedReason.title;
    }
    if (alternativeResourceUrl) {
      request.alternativeResourceUrl = alternativeResourceUrl;
    }
    if (selectedReason && this.checkedResources) {
      const resourcesToSend = [];
      this.checkedResources.forEach((resource) => {
        let url = (<Commands.OpenUrl>resource?.view?.title?.onClick)?.url;
        if (isWikiCard(resource)) {
          url = this.wikiCardsService.getCardUrl(resource?.resource?.data?.title, resource?.resource?.data?.id, true);
        }
        resourcesToSend.push({ name: resource.resource?.name, app: resource.resource?.appId, url });
      });
      request.selectedReasonResources = resourcesToSend;
    }
    this.resourcesService.feedback(request);
  }
}
